import type { ReactElement } from 'react';
import type { IconName } from '@forecastify/ui';
import type { MessageKey } from './i18n';
import { AgentSpendScreen } from './screens/agent-spend';
import { AuditScreen } from './screens/audit';
import { EnrolScreen } from './screens/enrol';
import { JoinScreen } from './screens/join';
import { OperationsScreen } from './screens/operations';
import { OperatorsScreen } from './screens/operators';
import { PlansScreen } from './screens/plans';
import { SignInScreen } from './screens/sign-in';
import { SignedOutScreen } from './screens/signed-out';
import { TenantDeleteScreen } from './screens/tenant-delete';
import { TenantDetailScreen } from './screens/tenant-detail';
import { TenantsScreen } from './screens/tenants';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE ROUTE TABLE IS DATA, so a test can enumerate it.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **DELIBERATELY NOT SHARING `apps/web`'s TABLE**, and the outgoing project's own words are
 * the reason: *"the two applications are separate deployables with separate principal sets, and a
 * shared table is the first step toward a shared session."* `sign-in` appears in both and means
 * different things. `decisions.md` S-6.
 *
 * ⚠️ **The SHAPE is the same, though, and that is not duplication for its own sake.** CLAUDE.md §4
 * names four route failures this project actually shipped, and every one of them is available to a
 * second application: a screen reachable only by typing a URL, a screen no marker can identify, a
 * route falling through to the catch-all, a stale pre-bundle. `entrance` and `marker` are what make
 * those checkable, and `__tests__/routes.test.tsx` here walks this table exactly as the product's
 * does.
 */

export interface RouteEntry {
  /** The address, exactly as a browser would show it. May contain `:params`. */
  readonly path: string;
  /**
   * Renders with no operator session.
   *
   * ⚠️ **`/sign-in`, `/enrol`, `/join/:token` and `/signed-out`, and no fifth.** Enrolment is the one that is
   * easy to get wrong: an operator who has not enrolled **cannot** have a session, so a guard that
   * required one would make the screen unreachable by exactly the people it is for.
   */
  readonly signedOutOk?: true;
  /**
   * A concrete address for a parameterised `path`.
   *
   * ⚠️ Required whenever `path` has a `:param`, because a test cannot render `/tenants/:id` — it
   * would land on the catch-all and report a failure about the test rather than about the app.
   */
  readonly example?: string;
  /** A `data-screen` value unique to this screen. The screen sets it on its outermost element. */
  readonly marker: string;
  /** For the document title, so a tab says where somebody is. */
  readonly title: MessageKey;
  /**
   * Whether somebody can get here by clicking.
   *
   * ⚠️ `'none'` demands a written reason. Seven screens once shipped in this project's predecessor
   * reachable only by typing a URL; a field that has to be filled in is what makes each one an
   * argued position rather than an omission.
   */
  readonly entrance: 'sidebar' | 'none';
  readonly unreachableBecause?: string;
  /** The sidebar row's label, when it has one. */
  readonly navLabel?: MessageKey;
  /**
   * ⚠️⚠️ **C1 · WHICH OF THE THREE SIDEBAR GROUPS THIS ROW SITS IN.** The redesign splits a
   * flat list of six into *Tenants*, *Operations* and *Oversight*, and the grouping is a fact
   * about the screen rather than a fact about the sidebar — so it is declared here, beside the
   * entrance, for the reason this file's header already gives: a navigation written by hand beside
   * a route table written by hand drifts on the first screen somebody adds in a hurry.
   *
   * ⚠️ **Required wherever `entrance` is `'sidebar'`**, and the route test asserts it. A row with
   * no group would be rendered under whichever heading the reduce happened to reach first.
   */
  readonly navGroup?: 'tenants' | 'operations' | 'oversight';
  /**
   * The sidebar row's glyph.
   *
   * ⚠️⚠️ **ADDED IN S7 BECAUSE `app.tsx` HARD-CODED `'building'` FOR EVERY ROW.** With two entries
   * that was merely imprecise — Plans already drew a building. With four it is a sidebar of
   * identical icons, which is worse than no icons at all: a glyph that does not distinguish is a
   * glyph that costs scanning time and returns nothing. Optional, so a route with no entrance needs
   * no answer.
   */
  readonly navIcon?: IconName;
  /**
   * `'bare'` renders with no shell around it, because the screen brings its own frame.
   *
   * ⚠️ Two nested shells means two `<nav>` landmarks on one page, which is an accessibility defect
   * and makes a reachability test ambiguous about which sidebar it is asking about.
   */
  readonly chrome: 'shell' | 'bare';
  readonly element: ReactElement;
}

export const ROUTES: readonly RouteEntry[] = [
  {
    path: '/tenants',
    marker: 'tenants',
    title: 'title.tenants',
    entrance: 'sidebar',
    navLabel: 'nav.tenants',
    navIcon: 'building',
    navGroup: 'tenants',
    chrome: 'shell',
    element: <TenantsScreen />,
  },
  {
    path: '/plans',
    marker: 'plans',
    title: 'title.plans',
    entrance: 'sidebar',
    navLabel: 'nav.plans',
    navIcon: 'credit-card',
    navGroup: 'tenants',
    chrome: 'shell',
    element: <PlansScreen />,
  },
  {
    path: '/operations',
    marker: 'operations',
    title: 'title.operations',
    entrance: 'sidebar',
    navLabel: 'nav.operations',
    navIcon: 'refresh-cw',
    navGroup: 'operations',
    chrome: 'shell',
    element: <OperationsScreen />,
  },
  {
    /* ⚠️ C27.5. Between operations and the audit trail, because it is the same kind of thing: a fact
     * about how the platform is being used that nobody could see before. */
    path: '/agent-spend',
    marker: 'agent-spend',
    title: 'title.spend',
    entrance: 'sidebar',
    navLabel: 'nav.spend',
    navIcon: 'sparkles',
    navGroup: 'operations',
    chrome: 'shell',
    element: <AgentSpendScreen />,
  },
  {
    path: '/audit',
    marker: 'audit',
    title: 'title.audit',
    entrance: 'sidebar',
    navLabel: 'nav.audit',
    navIcon: 'file-text',
    navGroup: 'oversight',
    chrome: 'shell',
    element: <AuditScreen />,
  },
  {
    /**
     * ⚠️⚠️ **C8 · UNDER *OVERSIGHT*, BESIDE THE AUDIT TRAIL, RATHER THAN UNDER *OPERATIONS*.**
     * Who may open this console is the same kind of fact as what they did with it: both are read
     * when somebody asks *"who could have done this"*. Filed under Operations it would sit beside
     * the job queue, which is work the platform does rather than a question about people.
     */
    path: '/operators',
    marker: 'operators',
    title: 'title.operators',
    entrance: 'sidebar',
    navLabel: 'nav.operators',
    navIcon: 'users',
    navGroup: 'oversight',
    chrome: 'shell',
    element: <OperatorsScreen />,
  },
  {
    /* ⚠️ `entrance: 'none'` and reachable — opened by clicking a tenant. A per-tenant sidebar link
     * is not a thing, which is the limit this field has in the product's table too. */
    path: '/tenants/:id',
    example: '/tenants/00000000-0000-4000-8000-000000000001',
    marker: 'tenant',
    title: 'title.tenant',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a tenant in the list. Reachable, but not by an entrance this field can describe — and an operator pasting this address into a ticket is why it is a real URL.',
    chrome: 'shell',
    element: <TenantDetailScreen />,
  },
  {
    /**
     * ⚠️⚠️ **C9 · ITS OWN ADDRESS, AND THAT IS THE POINT.** The mockup draws deletion as a whole
     * screen rather than a dialog, and the reason it has a URL is the same one the tenant detail
     * gives: an operator who is about to remove a customer's data is an operator who pastes the
     * address into a ticket first. A dialog has no address to paste.
     *
     * ⚠️ The confirmation screen that follows a successful delete is rendered BY this screen, not
     * at an address of its own — it describes a row that no longer exists, so a URL for it would
     * be one that works once and 404s on reload.
     */
    path: '/tenants/:id/delete',
    example: '/tenants/00000000-0000-4000-8000-000000000001/delete',
    marker: 'tenant-delete',
    title: 'title.tenantDelete',
    entrance: 'none',
    unreachableBecause:
      'Opened from the Permanent deletion tab of one organisation. A sidebar row for the one irreversible act in this console would be a sidebar row somebody clicks while looking for something else.',
    chrome: 'shell',
    element: <TenantDeleteScreen />,
  },
  {
    path: '/sign-in',
    marker: 'sign-in',
    title: 'title.signIn',
    signedOutOk: true,
    entrance: 'none',
    unreachableBecause:
      'Reached by not being signed in, not by a link — the chrome that would hold one only exists after signing in.',
    chrome: 'bare',
    element: <SignInScreen />,
  },
  {
    /**
     * ⚠️⚠️ **THE SCREEN THAT MUST BE REACHABLE WITHOUT A SESSION, BY SOMEBODY WHO CANNOT GET ONE.**
     * `scripts/superadmin.mjs create` and `reset-factor` leave an operator holding a password and no
     * factor, and a verified factor is mandatory with no grace period — so this is the way out of
     * that state. An INVITED operator never needs it: they enrol from the invitation (`/join`,
     * `decisions.md` S-21).
     */
    path: '/enrol',
    marker: 'enrol',
    title: 'title.enrol',
    signedOutOk: true,
    entrance: 'none',
    unreachableBecause:
      'Only the command-line support path leaves somebody needing it (create, reset-factor), and whoever ran the command sends the address. The sign-in card was asked to carry no registration-looking link (8 Oct 2026); invited operators enrol from their invitation (/join).',
    chrome: 'bare',
    element: <EnrolScreen />,
  },
  {
    /**
     * ⚠️⚠️ `0179` · **WHERE AN INVITED OPERATOR SIGNS UP, AND THE ONLY ENTRANCE IS AN EMAIL.** The
     * invitation used to be a product password reset followed by `/enrol`, which nobody was sent
     * to; this is both halves in one flow, and the code that finishes it also opens the session.
     */
    path: '/join/:token',
    example: '/join/an-example-invitation-token',
    marker: 'join',
    title: 'title.join',
    signedOutOk: true,
    entrance: 'none',
    unreachableBecause:
      'Opened from the invitation email, whose link carries the token. Nobody without a token has anything to do here, and anybody with one already has the link.',
    chrome: 'bare',
    element: <JoinScreen />,
  },
  {
    path: '/signed-out',
    marker: 'signed-out',
    title: 'title.signedOut',
    signedOutOk: true,
    entrance: 'none',
    unreachableBecause:
      'Where an ended session lands. Navigated to by the session layer, never by a control — and typing it while signed in renders the sign-in card, because that is what having no session looks like.',
    chrome: 'bare',
    element: <SignedOutScreen />,
  },
];

/** Where an operator lands. The list is the console. */
export const HOME_PATH = '/tenants';

/**
 * ⚠️ **One function per address with a parameter in it**, for the reason the constants above
 * exist: `/tenants/${id}` written out at each call site is a string nobody can grep for when the
 * shape changes, and `encodeURIComponent` is the part that gets left off the third copy.
 */
export const tenantPath = (id: string): string => `/tenants/${encodeURIComponent(id)}`;
export const tenantDeletePath = (id: string): string => `${tenantPath(id)}/delete`;
export const TENANTS_PATH = '/tenants';
export const PLANS_PATH = '/plans';
export const OPERATIONS_PATH = '/operations';
export const AUDIT_PATH = '/audit';
export const OPERATORS_PATH = '/operators';

/**
 * ⚠️ Its own constant for the product table's reason: `App` renders it, the sign-in card links to
 * it, and the route test asserts both halves. Three places, one string.
 */
export const SIGN_IN_PATH = '/sign-in';
export const ENROL_PATH = '/enrol';
export const SIGNED_OUT_PATH = '/signed-out';
